import React, { useState, useReducer } from 'react';
import { pick } from 'lodash';
import { useQuery } from '@apollo/client';
import { useIntl } from 'react-intl';
import { Page, Checkbox } from '@superops/ui-design-system';
import {
  CustomModal,
  Loader,
  SaveCard,
  DetailCard,
  AccessControl,
  getCustomViewValues,
  reactSelectInlineError,
  preFormatForm,
  validateForm,
  ViewTableColumns,
  getTranslatedText,
  COMMON_CONSTANTS
} from '@nile/components';
import AdvancedSearch from '@nile/components/src/Components/AdvancedSearch/AdvancedSearch';
import {
  GET_ENTITY_PROPERTIES,
  GET_RULEBUILDER_OPERATORS
} from '@nile/graphql/src';
import { getColumns } from '@nile/client_management/src/Containers/InvoiceListColumns';

const CustomViewForm = (props) => {
  const { viewId, module, actionType, actions, ...restEditProps } = props;

  const {
    roleToCheck,
    checkboxLabel,
    getModuleMeta,
    moduleMetaInput,
    moduleMetaClient,
    moduleMetaResponseKey,
    onAttributeSelect,
    onValueSearch,
    constraints,
    showColumns
  } = getCustomViewValues(module);

  const intl = useIntl();
  const [submitLoading, setSubmitLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [viewData, setViewData] = useState({
    viewName: restEditProps?.viewName || '',
    displayIcon: restEditProps?.displayIcon || '😄',
    isSharedChecked: restEditProps?.isSharedChecked ?? false
  });

  function handleViewDataChange(field) {
    return (value) => {
      setViewData((prev) => ({
        ...prev,
        [field]: value
      }));
    };
  }

  const [viewCondition, setViewCondition] = useReducer(
    (state, newState) => ({
      ...state,
      ...newState
    }),
    restEditProps?.viewCondition
      ? {
          ruleCondition: restEditProps.viewCondition
        }
      : {
          ruleCondition: { joinOperator: 'AND', operands: [] }
        }
  );

  const [tableColumnsFields, setTableColumnsFields] = useState([]);
  // const updateSharedView = useCallback(
  //   (selected) => {
  //     handleViewDataChange('isSharedChecked')(selected);
  //     // setIsSharedChecked(selected);
  //   },
  //   [actionType]
  // );

  const { loading: entitiesLoading, data: allEntities } = useQuery(
    GET_ENTITY_PROPERTIES
  );

  const { loading: viewMetaLoading, data: metaData } = useQuery(getModuleMeta, {
    variables: moduleMetaInput ? { input: moduleMetaInput } : {},
    client: moduleMetaClient,
    fetchPolicy: COMMON_CONSTANTS.CACHE_POLICIES.NO_CACHE
  });

  const { loading: operatorsLoading, data: allOperators } = useQuery(
    GET_RULEBUILDER_OPERATORS
  );

  const { columns, selectedColumns, availableColumns } = getColumns({
    viewId: viewId,
    module: module
  });

  const handleCreateEditViewSave = () => {
    const viewInput = {
      input: {
        name: viewData?.viewName,
        viewCondition: viewCondition.ruleCondition,
        shared: viewData?.isSharedChecked,
        module: module.toUpperCase(),
        displayIcon: viewData?.displayIcon
      },
      columnPersonalizationInput: tableColumnsFields
    };
    const dummyForm = pick(viewInput.input, ['name', 'shared']);
    // eslint-disable-next-line no-underscore-dangle
    const _dummyForm = preFormatForm(dummyForm);
    const validationResult = validateForm(
      {
        ..._dummyForm,
        viewCondition: viewCondition.ruleCondition
      },
      constraints
    );

    if (validationResult) {
      return setErrors(validationResult);
    }
    setErrors({});

    setSubmitLoading(true);
    let primaryAction = actionType === 'edit' ? 'editAction' : 'addAction';
    actions?.[primaryAction]?.(viewInput)?.finally((id) => {
      setSubmitLoading(false);
      actions.back();
    });
  };

  return (
    <div className="vh80 vw70">
      <Page
        variant="primary"
        title={getTranslatedText(intl, `customview.${actionType}`)}
        primaryAction={
          <SaveCard
            handleSave={handleCreateEditViewSave}
            actions={actions}
            loading={submitLoading}
            removeSecondaryBtnBg
          />
        }
        contentHeight="100px"
      >
        <DetailCard
          actions={{
            back: actions.back,
            updateName: handleViewDataChange('viewName'),
            updateEmoji: handleViewDataChange('displayIcon')
          }}
          disableActive
          noDesciption
          notSectioned
          data={{ name: viewData?.viewName, emoji: viewData?.displayIcon }}
          errors={errors?.name}
          useEmojiPicker={true}
          noCard
        >
          <AccessControl
            roleToCheck={roleToCheck?.sharedviewId || ''}
            render={
              <div className="mt-20">
                <Checkbox
                  label={getTranslatedText(intl, checkboxLabel || '')}
                  id="customViews"
                  name="customViews"
                  checked={viewData?.isSharedChecked}
                  onChange={handleViewDataChange('isSharedChecked')}
                />
              </div>
            }
          />
        </DetailCard>
        <div className="mt40 mb-40">
          {entitiesLoading || viewMetaLoading || operatorsLoading ? (
            <div className="fw fhp bg-white br10">
              <Loader />
            </div>
          ) : (
            <AdvancedSearch
              meta={metaData[moduleMetaResponseKey]}
              allEntities={allEntities}
              allOperators={allOperators}
              context={{
                updateAutomationRule: (formattedRule, key) => {
                  setViewCondition({ [key]: formattedRule });
                }
              }}
              query={viewCondition.ruleCondition}
              errors={errors?.['viewCondition.operands']}
              currentModule={module.toLowerCase()}
              onAttributeSelect={onAttributeSelect}
              onValueSearch={onValueSearch}
              isNewBuilder
            />
          )}
          {typeof errors?.['viewCondition.operands']?.[0] === 'string' &&
            reactSelectInlineError(errors?.['viewCondition.operands'])}
        </div>
        {showColumns && !entitiesLoading && (
          <ViewTableColumns
            module={module}
            columns={columns}
            selectedColumns={selectedColumns}
            availableColumns={availableColumns}
            tableColumnsFields={tableColumnsFields}
            setTableColumnsFields={setTableColumnsFields}
          />
        )}
      </Page>
    </div>
  );
};

function CustomViewModal({
  isOpen,
  actions,
  isModuleViewLoading,
  ...restProps
}) {
  return (
    <CustomModal open={isOpen} onClose={actions.back}>
      {isModuleViewLoading ? (
        <div className="vh80 vw70 bg-white br10">
          <Loader />
        </div>
      ) : (
        <CustomViewForm {...restProps} actions={actions} />
      )}
    </CustomModal>
  );
}

export default CustomViewModal;
